import { Component, OnInit, ViewChild } from '@angular/core';
import { FeedMessageService, UserService, UtilsService, showErrorWarningAndSuccess, showSuccess, PermissionsService, CustomValidators, LanguageService, showErrorAndWarning } from '@services/index.service';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { environment } from '@environments/environment';
import { TranslateService } from '@ngx-translate/core';
import { AbstractControl, NgForm, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AngularPaths, AuthUserPinType, LanguageResponse, UserLanguageRequest, Permission, Response, UserData, UserPasswordRequest, UserPinRequest, Consumer, UserPinConfig, DomainType, UserSession } from '@models/index.model';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ConfirmUserPinComponent } from '@shared/index.shared';
import { Router } from '@angular/router';

@Component({
    selector: 'app-user-profile',
    templateUrl: './user-profile.component.html',
    styleUrls: [],
    standalone: false
})

export class UserProfileComponent implements OnInit {
  public isMobile: boolean;
  public currentLang: string;
  public theme = environment.THEME;
  public sendingRequest: boolean = true;
  public sendingUserData: boolean;
  // Permissions
  public nameEdit: boolean = false;
  public emailEdit: boolean = false;
  // User Language
  public availableLangs: LanguageResponse[];
  public sendingLanguageData: boolean;
  // User Data
  public changeUserDataForm: UntypedFormGroup;
  public _userData: UserData;
  // Spinners
  public sendingPassword: boolean;
  public sendingPin: boolean;
  public loadingData: boolean;
  // Password
  public editPasswordPermission: boolean = false;
  public hidePassCurrent: boolean = true;
  public hidePassNew: boolean = true;
  public hidePassRepeat: boolean = true;
  public hideCurrentUserPin: boolean = true;
  public hideUserPinNew: boolean = true;
  public hideUserPinRepeat: boolean = true;
  public changeUserPasswordForm: UntypedFormGroup;
  public changeUserPinForm: UntypedFormGroup;
  @ViewChild('passwordNgForm') passwordNgForm: NgForm;
  @ViewChild('pinNgForm') pinNgForm: NgForm;
  public currentForm: number = 0;

  constructor(
    public dialog: MatDialog,
    public dialogRef: MatDialogRef<UserProfileComponent>,
    private _notify: FeedMessageService,
    private _translate: TranslateService,
    private _userServ: UserService,
    private _utils: UtilsService,
    private _permiss: PermissionsService,
    private _fb: UntypedFormBuilder,
    private _lang: LanguageService,
    private _router: Router,
  ) {
    this.currentLang = this._translate.currentLang;
    this.theme = this._utils.titleCaseWord(this.theme);
    this.isMobile = environment.platform === 'mobile';
  }

  ngOnInit(): void {
    this.nameEdit = this._permiss.hasPermiss(Permission.CHANGE_NAME);
    this.emailEdit = this._permiss.hasPermiss(Permission.CHANGE_EMAIL);
    this.getUserData();
    this.availableLangs = this._lang.getDefaultSupportedLanguages();
    this.editPasswordPermission = this._permiss.hasPermiss(Permission.CHANGE_PASSWORD);
    this.changeUserPasswordForm = this._initPasswordForm();
    this.changeUserPinForm = this._initPinForm();
  }

  public tabChanged(tabChangeEvent: MatTabChangeEvent): void {
    this.currentForm = tabChangeEvent.index;
  }
  public expansionChanged(isExpanded: boolean, index): void {
    if (isExpanded && (index !== this.currentForm)) {
      this.currentForm = index;
    }
    if (!isExpanded && (index === this.currentForm)) {
      this.currentForm = -1;
    }
  }

  public initUserData(userData: UserData) {
    this._userData = userData;
    this.changeUserDataForm = this._initUserDataForm(
      this._userData.friendlyName,
      this._userData.email,
      this._lang.normalizeLang(this._userData.language)
    );
    this._userServ.setUserDataToLocalStorage(userData);
    this._userServ.setUserDataLanguageToLocalStorage(this._lang.normalizeLang(this._userData.language));
    this._lang.configureApplicationLanguage();
    this.changeUserDataForm.markAsPristine();
  }

  private _initUserDataForm(friendlyName: string, email: string, language: string) {
    const userForm: UntypedFormGroup = this._fb.group({
      friendlyName: [friendlyName],
      email: [email],
      language: [language, Validators.required],
    });

    const userControls = userForm.controls;
    if (this.nameEdit) {
      userControls.friendlyName.setValidators(Validators.required);
      userControls.friendlyName.updateValueAndValidity();
    }
    if (this.emailEdit) {
      userControls.email.setValidators([Validators.required, Validators.email]);
      userControls.email.updateValueAndValidity();
    }

    return userForm;
  }

  public isFieldRequired(fieldControl: AbstractControl): boolean {
    return (fieldControl.dirty || fieldControl.touched) && !fieldControl.value;
  }

  public changeUserData() {
    const userDataFormControls = this.changeUserDataForm.controls;
    const userDataRequest: UserData = {
      friendlyName: userDataFormControls.friendlyName.value,
      email: userDataFormControls.email.value,
      language: userDataFormControls.language.value === 'pt'? 'pt-BR' : userDataFormControls.language.value,
    };

    this.sendingUserData = true;
    this._userServ.changeUserData(userDataRequest).pipe(
      tap((res: Response<UserData>) => this._notify.feedBackResult(res.messageType, res.message, showErrorWarningAndSuccess)),
      map((res: Response<UserData>) => res.data),
      finalize(() => (this.sendingUserData = false))
    ).subscribe((userData: UserData) => {
      this.initUserData(userData);
    });
  }

  public getUserData() {
    this.loadingData = true;
    this._userServ.getUserData().pipe(
      tap((res: Response<UserData>) => this._notify.feedBackResult(res.messageType, res.message, showErrorAndWarning)),
      map((res: Response<UserData>) => res.data),
      finalize(() => (this.loadingData = false))
    ).subscribe((userData: UserData) => {
      this.initUserData(userData);
    });
  }

  public changeLanguage() {
    const userDataFormControls = this.changeUserDataForm.controls;
    const userLanguageRequest: UserLanguageRequest = {
      language: userDataFormControls.language.value === 'pt'? 'pt-BR' : userDataFormControls.language.value,
    };

    this.sendingUserData = true;
    this._userServ.changeUserLanguage(userLanguageRequest).pipe(
      tap((res: Response<UserLanguageRequest>) => this._notify.feedBackResult(res.messageType, res.message, showErrorWarningAndSuccess)),
      map((res: Response<UserLanguageRequest>) => res.data),
      finalize(() => (this.sendingUserData = false))
    ).subscribe((lang: any) => {
      this._userServ.setUserDataLanguageToLocalStorage(lang.value);
      this._lang.configureApplicationLanguage();
      this.changeUserDataForm.markAsPristine();
    });
  }

  private _initPasswordForm(): UntypedFormGroup {
    return this._fb.group(
      {
        password: [null, Validators.required],
        newPassword: [null, Validators.required],
        newPasswordConfirm: [null, Validators.required],
      },
      {
        validators: CustomValidators.fieldsMatch(['newPassword', 'newPasswordConfirm']),
      }
    );
  }

  private _initPinForm(): UntypedFormGroup {
    return this._fb.group(
      {
        currentUserPin: [null, Validators.required],
        newPin: [null, Validators.required],
        newPinConfirm: [null, Validators.required],
      },
      {
        validators: CustomValidators.fieldsMatch(['newPin', 'newPinConfirm']),
      }
    );
  }

  public changeUserCredentials() {
    const passwordRequest: UserPasswordRequest = {
      password: this.changeUserPasswordForm.controls.password.value,
      newPassword: this.changeUserPasswordForm.controls.newPassword.value,
    };
    this._changeUserCredentialsRequest(passwordRequest).subscribe();
  }

  private _changeUserCredentialsRequest(userCredentialsRequest: UserPasswordRequest) {
    this.sendingPassword = true;
    return this._userServ.changeLoggedUserPassword(userCredentialsRequest).pipe(
      tap((res: Response<any>) => {
        this.passwordNgForm.resetForm();
        this._notify.feedBackResult(res.messageType, res.message, showSuccess);
      }),
      catchError(err => {
        return of(err);
      }),
      finalize(() => {
        this.sendingPassword = false;
      })
    );
  }

  public changeUserPin() {
    const pinRequest: UserPinRequest = {
      currentUserPin: this.changeUserPinForm.controls.currentUserPin.value,
      userPin: this.changeUserPinForm.controls.newPin.value,
    };
    this._changeUserPinRequest(pinRequest).subscribe();
  }

  private _changeUserPinRequest(userPinRequest: UserPinRequest) {
    this.sendingPin = true;
    return this._userServ.changeUserPin(userPinRequest).pipe(
      tap((res: Response<any>) => {
        this.pinNgForm.resetForm();
        this._notify.feedBackResult(res.messageType, res.message, showSuccess);
      }),
      catchError(err => {
        return of(err);
      }),
      finalize(() => {
        this.sendingPin = false;
      })
    );
  }

  public setAndResetUserPin(type: AuthUserPinType) {
    const dialogRef = this.dialog.open(ConfirmUserPinComponent, {
      width: '600px',
      data: {
        type
      },
    });
    dialogRef.disableClose = true;
    dialogRef.afterClosed().subscribe(res => {
      // get from session (domainName y domainType)
      const userData: UserData = this._userServ.getUserDataFromLocalStorage();
      const config: UserPinConfig = {
        dn: userData.domainName,
        dt: userData.domainType,
        op: type,
        c: Consumer.GLOBAL_PERSONAL_AREA
      };
      const userPinParam = this._utils.stringifyUserPreferenceConfig(config);
      if (res) {
        // redirect
        let url = '';
        if (environment.production) {
          url = this._router.serializeUrl(this._router.createUrlTree([`/personal/${AngularPaths.USER_PIN}/${userPinParam}`]));
        } else {
          url = this._router.serializeUrl(this._router.createUrlTree([`/${AngularPaths.USER_PIN}/${userPinParam}`]));
        }
        window.open(url, '_blank');
        this.closeDialog();
      }
    });
  }


  public closeDialog(): void {
    this.dialogRef.close();
  }
}
